import { useAuthStore, useCandidatesStore } from "@/stores";
import type { RouteRecordRaw } from "vue-router";

export default [
  {
    name: "announcements",
    path: "/announcements",
    component: () => import("@/views/Admin/Announcements.vue"),
    meta: {
      requiresAuth: true,
      authorizedRoles: ["Admin"],
      friendlyName: "Announcements",
    },
  },
  {
    name: "award-settings",
    path: "/award-settings",
    component: () => import("@/views/Admin/AwardSettings/AwardSettings.vue"),
    meta: {
      requiresAuth: true,
      authorizedRoles: ["Admin"],
      friendlyName: "Award Settings",
    },
  },
  {
    name: "candidates",
    path: "/candidates",
    component: () => import("@/views/Admin/Candidates.vue"),
    meta: {
      requiresAuth: true,
      authorizedRoles: ["Admin"],
      friendlyName: "Candidates",
    },
  },
  {
    path: "/candidates/:id",
    name: "candidate-details",
    props: true,
    component: () => import("@/views/Admin/CandidateDetails.vue"),
    meta: {
      requiresAuth: true,
      authorizedRoles: ["Admin"],
      friendlyName: "Candidate",
    },
  },
  {
    path: "/candidates/:id/home/:email",
    name: "admin-candidate-home",
    props: true,
    component: () => import("@/views/Candidate/CandidateHome.vue"),
    meta: {
      requiresAuth: true,
      authorizedRoles: ["Admin"],
      friendlyName: "Candidate Home",
    },
    beforeEnter: async (to, _from, next) => {
      const authStore = useAuthStore();
      const candidatesStore = useCandidatesStore();
      const candidateId = to.params.id as string;
      const email = to.params.email as string;
      if (!candidateId || !email) {
        return next({ name: "home" });
      }
      await candidatesStore.getCandidate(candidateId);
      const { contacts = [] } = candidatesStore.currentCandidate;
      const contact = contacts.find(
        (c) => c.user?.email.toLocaleLowerCase() === email.toLocaleLowerCase(),
      );
      if (contact?.user) {
        authStore.masqueradeUser = {
          ...contact.user,
          role: "Candidate",
          roles: ["Candidate"],
        };
        return next();
      }
      authStore.masqueradeUser = {
        ...authStore.user,
        role: "Candidate",
        roles: ["Candidate"],
      };
      return next();
    },
  },
  {
    path: "/categories",
    name: "categories",
    component: () => import("@/views/Admin/Categories.vue"),
    meta: {
      requiresAuth: true,
      authorizedRoles: ["Admin"],
      friendlyName: "Categories",
    },
  },
  {
    name: "settings",
    path: "/settings",
    component: () => import("@/views/Admin/Settings.vue"),
    meta: {
      requiresAuth: true,
      authorizedRoles: ["Admin"],
      friendlyName: "Settings",
    },
  },
  {
    path: "/contacts/:PK",
    name: "contact-details",
    props: true,
    component: () => import("@/views/Admin/ContactDetails.vue"),
    meta: {
      requiresAuth: true,
      authorizedRoles: ["Admin"],
      friendlyName: "Contact",
    },
  },
  {
    path: "/tags",
    name: "tags",
    component: () => import("@/views/Admin/Tags.vue"),
    meta: {
      requiresAuth: true,
      authorizedRoles: ["Admin"],
      friendlyName: "Tags",
    },
  },
  {
    path: "/users",
    name: "users",
    component: () => import("@/views/Admin/Users.vue"),
    meta: {
      requiresAuth: true,
      authorizedRoles: ["Admin"],
      friendlyName: "Users",
    },
  },
  {
    path: "/verify-existing-nominees",
    name: "verify-existing-nominees",
    component: () => import("@/views/Admin/VerifyExistingNominees.vue"),
    meta: {
      requiresAuth: true,
      authorizedRoles: ["Verification"],
      friendlyName: "Verify Existing Nominees",
    },
  },
  {
    path: "/verify-new-nominees",
    name: "verify-new-nominees",
    component: () => import("@/views/Admin/VerifyNewNominees.vue"),
    meta: {
      requiresAuth: true,
      authorizedRoles: ["Verification"],
      friendlyName: "Verify New Nominees",
    },
  },
  // {
  //   path: "/preselect-candidates",
  //   name: "preselect-candidates",
  //   component: () => import("@/views/Admin/PreselectCandidates.vue"),
  //   meta: {
  //     requiresAuth: true,
  //     authorizedRoles: [Role.Admin],
  //     friendlyName: "Preselect Candidates",
  //   },
  // },
  {
    path: "/preselect-nominees",
    name: "preselect-nominees",
    component: () => import("@/views/Admin/PreselectNominees.vue"),
    meta: {
      requiresAuth: true,
      authorizedRoles: ["Admin"],
      friendlyName: "Preselect Nominees",
    },
  },
  {
    path: "/quick-nominate",
    name: "quick-nominate",
    component: () => import("@/views/Admin/QuickNominate.vue"),
    meta: {
      requiresAuth: true,
      authorizedRoles: ["Admin"],
      friendlyName: "Quick Nominate",
    },
  },
  {
    path: "/exports",
    name: "exports",
    component: () => import("@/views/Admin/Exports.vue"),
    meta: {
      requiresAuth: true,
      authorizedRoles: ["Admin"],
      friendlyName: "Exports",
    },
  },
  {
    path: "/finalize-nominees",
    name: "finalize-nominees",
    component: () => import("@/views/Admin/FinalizeNominees.vue"),
    meta: {
      requiresAuth: true,
      authorizedRoles: ["Admin"],
      friendlyName: "Finalize Nominees",
    },
  },
  {
    path: "/finalize-finalists",
    name: "finalize-finalists",
    component: () => import("@/views/Admin/FinalizeFinalists.vue"),
    meta: {
      requiresAuth: true,
      authorizedRoles: ["Admin"],
      friendlyName: "Finalize Finalists",
    },
  },
  {
    path: "/finalize-winners",
    name: "finalize-winners",
    component: () => import("@/views/Admin/FinalizeWinners.vue"),
    meta: {
      requiresAuth: true,
      authorizedRoles: ["Admin"],
      friendlyName: "Finalize Winners",
    },
  },
  {
    path: "/finalize-master-categories",
    name: "finalize-master-categories",
    component: () => import("@/views/Admin/FinalizeMasterCategories.vue"),
    meta: {
      requiresAuth: true,
      authorizedRoles: ["Admin"],
      friendlyName: "Finalize Master Categories",
    },
  },
  {
    path: "/initial-setup",
    name: "initial-setup",
    component: () => import("@/views/Admin/InitialSetup.vue"),
    meta: {
      requiresAuth: true,
      authorizedRoles: ["Admin"],
      friendlyName: "Initial Setup",
    },
  },
  {
    path: "/merge-candidates/:candidateId1",
    name: "merge-candidates",
    props: true,
    component: () => import("@/views/Admin/MergeCandidates.vue"),
    meta: {
      requiresAuth: true,
      authorizedRoles: ["Admin"],
      friendlyName: "Merge Candidates",
    },
  },
  {
    path: "/claim-nominations",
    name: "claim-nominations",
    component: () => import("@/views/Admin/ClaimNominations.vue"),
    meta: {
      requiresAuth: true,
      authorizedRoles: ["Admin"],
      friendlyName: "Reconcile Claimed Nomination Data",
    },
  },
  {
    path: "/contacts",
    name: "contacts",
    component: () => import("@/views/Admin/Contacts.vue"),
    meta: {
      requiresAuth: true,
      authorizedRoles: ["Admin"],
      friendlyName: "Contacts",
    },
  },
  {
    path: "/campaigns",
    name: "campaigns",
    component: () => import("@/views/Admin/Campaigns.vue"),
    meta: {
      requiresAuth: true,
      authorizedRoles: ["Campaigns"],
      friendlyName: "Campaigns",
    },
  },
  {
    path: "/products",
    name: "products",
    component: () => import("@/views/Admin/Products/Products.vue"),
    meta: {
      requiresAuth: true,
      authorizedRoles: ["Admin"],
      friendlyName: "Products",
    },
  },
  {
    path: "/products/:id",
    name: "edit-product",
    props: true,
    component: () => import("@/views/Admin/Products/EditProduct.vue"),
    meta: {
      requiresAuth: true,
      authorizedRoles: ["Admin"],
      friendlyName: "Edit Product",
    },
  },
  {
    path: "/awards-ceremony-registration",
    name: "awards-ceremony-registration",
    component: () => import("@/views/Admin/AwardsCeremonyRegistration.vue"),
    meta: {
      requiresAuth: true,
      authorizedRoles: ["Admin"],
      friendlyName: "Awards Ceremomy Registration",
      hideNav: true,
    },
  },
  {
    path: "/reports",
    name: "reports",
    component: () => import("@/views/Admin/Reports.vue"),
    meta: {
      requiresAuth: true,
      authorizedRoles: ["Admin"],
      friendlyName: "Reports",
    },
  },
  {
    path: "/automations",
    name: "automations",
    component: () => import("@/views/Admin/Automations.vue"),
    meta: {
      requiresAuth: true,
      authorizedRoles: ["Automations"],
      friendlyName: "Automations",
    },
  },
] as RouteRecordRaw[];

// export default routes;
